/**
 * AxisRangeControl - Renders axis range selection control
 *
 * Displays a SelectableAxis component for choosing a range of values
 * along an axis (e.g., step range in grid mode). Used for filtering/zooming.
 */

import {expandRangeToNElements} from '@wandb/weave/common/util/data';
import React from 'react';

import {canRenderSelectableAxis, SelectableAxis} from '../../Axis';
import {getColumnCountFromConfig} from '../layout/renderers/rendererUtils';
import {type AxisRangeControlProps} from './types';

export type {AxisRangeControlProps};

const AXIS_RANGE_CONTROL_HORIZONTAL_PADDING = 8;

/**
 * Renders an axis range selection control.
 *
 * @param props - AxisRangeControlProps
 * @returns React element
 */
export const AxisRangeControl: React.FC<AxisRangeControlProps> = ({
  axis,
  axisLabel,
  gridData,
  config,
  width,
  updateConfig,
  direction = 'bottom',
}) => {
  const columnCount = getColumnCountFromConfig(config);
  const ticks =
    axis === 'xAxis'
      ? gridData.metadata.xAxisTicks
      : gridData.metadata.yAxisTicks;

  const allAxisValues =
    axis === 'xAxis'
      ? gridData.metadata.allXAxisValues
      : gridData.metadata.allYAxisValues;

  const selection =
    axis === 'xAxis' ? config.selection?.xAxis : config.selection?.yAxis;

  const handleSelect = React.useCallback(
    (range: [number, number]) => {
      // Use ref to get current config to avoid stale closures
      const currentConfig = config;

      if (axis === 'xAxis' && columnCount && allAxisValues) {
        // For x-axis with column count, expand range to fill columns
        const newRange = expandRangeToNElements(
          range,
          allAxisValues,
          columnCount
        );

        // Always update config to persist changes
        // SelectableAxis will handle its own internal state
        const newSelection = {...currentConfig.selection, xAxis: newRange};

        updateConfig({
          selection: newSelection,
        });

        return newRange;
      }

      // For all other cases, we still need to update config to persist the selection
      // (SelectableAxis syncs to props.selection on each render via useEffect)
      // Always update to persist
      const newSelection = {...currentConfig.selection, [axis]: range};

      updateConfig({
        selection: newSelection,
      });

      return range;
    },
    [axis, columnCount, config, allAxisValues, updateConfig]
  );

  const handleClear = React.useCallback(() => {
    const currentConfig = config;

    const newSelection = {
      ...currentConfig.selection,
      [axis]: undefined,
    };

    updateConfig({
      selection: newSelection,
    });
  }, [axis, config, updateConfig]);

  // Don't render if we don't have the required data (match original behavior)
  if (!ticks || !allAxisValues) {
    return null;
  }

  // Validate selection - ensure it doesn't have undefined values
  const validSelection: [number, number] | undefined =
    selection &&
    Array.isArray(selection) &&
    selection.length === 2 &&
    typeof selection[0] === 'number' &&
    typeof selection[1] === 'number'
      ? (selection as [number, number])
      : undefined;

  if (width == null || width <= 0) {
    return null;
  }

  const paddedWidth = Math.max(
    0,
    Math.floor(width) - AXIS_RANGE_CONTROL_HORIZONTAL_PADDING * 2
  );

  if (paddedWidth <= 0) {
    return null;
  }

  const shouldRenderAxis = canRenderSelectableAxis(paddedWidth);

  return (
    <div className={shouldRenderAxis ? 'w-full px-8' : undefined}>
      <SelectableAxis
        axisLabel={axisLabel}
        ticks={allAxisValues}
        direction={direction}
        width={paddedWidth}
        selection={validSelection}
        onSelect={handleSelect}
        onClear={handleClear}
      />
    </div>
  );
};
